import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Input } from "@/components/atoms";
import { FormField } from "@/components/molecules/FormField";
import {
  clearInvestorSession,
  getInvestorSession,
  investorAPI,
  saveInvestorSession,
} from "@/services/api/investor.service";
import { InvestorFinancialDashboard } from "@/components/investor/InvestorFinancialDashboard";
import { AppLogo } from "@/components/atoms/AppLogo";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { applyPageMeta, clearPageMeta } from "@/utils/page-meta.utils";
import { buildInvestorShareTitle } from "@/utils/investor-share-meta.utils";

const DEFAULT_PAGE_TITLE = "BurnChart AI | Soki Creative LLC";
const DEFAULT_PAGE_DESCRIPTION =
  "BurnChart AI — interactive startup runway modeling by Soki Creative LLC. Plan burn, funding, and growth with clarity.";

export function InvestorPortalPage() {
  const { token: rawToken = "" } = useParams();
  const token = decodeURIComponent(rawToken);
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState<Awaited<ReturnType<typeof investorAPI.getLinkInfo>> | null>(
    null,
  );
  const [loadError, setLoadError] = useState<string | null>(null);
  const [session, setSession] = useState(() => getInvestorSession(token));
  const [passcode, setPasscode] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<Record<string, unknown> | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const linkInfo = await investorAPI.getLinkInfo(token);
        if (!cancelled) {
          setInfo(linkInfo);
          setLoadError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setInfo(null);
          setLoadError(err instanceof Error ? err.message : "Failed to load investor link");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (!info) {
      return;
    }

    applyPageMeta({
      title: buildInvestorShareTitle(info.startupName),
      description: info.description ?? info.tagline ?? undefined,
    });

    return () => {
      clearPageMeta(DEFAULT_PAGE_TITLE, DEFAULT_PAGE_DESCRIPTION);
    };
  }, [info]);

  useEffect(() => {
    if (!session) {
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const payload = await investorAPI.getData(session);
        if (!cancelled) {
          setData(payload);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load data");
          clearInvestorSession(token);
          setSession(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [session, token]);

  const handleVerify = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    try {
      const result = await investorAPI.verifyAccess(token, {
        passcode,
        displayName: displayName.trim(),
        email: email.trim() || undefined,
      });
      const nextSession = {
        ...result,
        token,
        displayName: displayName.trim(),
        email: email.trim() || null,
      };
      saveInvestorSession(token, nextSession);
      setSession(nextSession);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Verification failed");
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-page text-white">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-brand border-t-transparent" />
      </div>
    );
  }

  if (!info) {
    return (
      <UnavailableScreen
        title="Link not found"
        message={
          loadError ??
          "This investor link is invalid. Ask the founder to regenerate and resend the URL."
        }
      />
    );
  }

  if (!info.available && !session) {
    return (
      <UnavailableScreen
        title={info.reason === "revoked" ? "Link revoked" : "Link expired"}
        message="This share link is no longer active."
        contact={info.investorContact}
        startupName={info.startupName}
      />
    );
  }

  if (!session) {
    return (
      <div className="flex min-h-screen min-w-0 flex-col overflow-x-hidden bg-page text-white">
        <header className="border-b border-white/10 px-4 py-6 text-center sm:px-6 sm:py-8">
          <div className="mb-4 flex justify-center">
            <AppLogo to="/" size={40} showWordmark={false} />
          </div>
          <p className="text-xs font-semibold uppercase tracking-wide text-brand">
            Investor view
          </p>
          <h1 className="mt-2 text-2xl font-black sm:text-3xl">{info.startupName}</h1>
          {info.tagline ? (
            <p className="mt-2 text-sm text-white/60">{info.tagline}</p>
          ) : null}
          <p className="mt-1 text-xs text-white/45">{info.label}</p>
        </header>

        <main className="mx-auto w-full max-w-md flex-1 px-4 py-10">
          <form onSubmit={handleVerify} className="space-y-4 rounded-[20px] border border-white/10 bg-surface p-6">
            <FormField label="Your name (required)">
              <Input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Jane Investor"
                required
              />
            </FormField>
            <FormField label="Email (optional)">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="jane@fund.com"
              />
            </FormField>
            <FormField label="6-digit PIN">
              <Input
                value={passcode}
                onChange={(e) =>
                  setPasscode(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
                placeholder="123456"
                required
              />
            </FormField>
            {error ? <div className="text-sm text-danger">{error}</div> : null}
            <Button type="submit" variant="primary" size="md" className="w-full">
              View financials
            </Button>
          </form>
        </main>
        <SiteFooter
          variant="compact"
          className="mt-auto"
          showBrandMark
          extraNote="Made with burn-chart.com by Soki Creative LLC"
        />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-page text-white">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-brand border-t-transparent" />
      </div>
    );
  }

  return (
    <InvestorFinancialDashboard data={data} session={session} />
  );
}

function UnavailableScreen({
  title,
  message,
  contact,
  startupName,
}: {
  title: string;
  message: string;
  contact?: Record<string, string> | null;
  startupName?: string;
}) {
  return (
    <div className="flex min-h-screen flex-col bg-page text-white">
      <main className="mx-auto flex max-w-lg flex-1 flex-col justify-center px-4 py-16 text-center">
        {startupName ? (
          <p className="text-sm font-semibold text-brand">{startupName}</p>
        ) : null}
        <h1 className="mt-2 text-2xl font-black">{title}</h1>
        <p className="mt-3 text-sm text-white/65">{message}</p>
        {contact ? (
          <div className="mt-8 rounded-[20px] border border-white/10 bg-surface p-6 text-left text-sm">
            <p className="font-semibold text-white">Contact the founder</p>
            {contact.message ? (
              <p className="mt-2 text-white/65">{contact.message}</p>
            ) : null}
            <div className="mt-4 space-y-1 text-white/75">
              {contact.name ? <div>{contact.name}</div> : null}
              {contact.email ? (
                <div>
                  <a href={`mailto:${contact.email}`} className="text-brand">
                    {contact.email}
                  </a>
                </div>
              ) : null}
              {contact.phone ? <div>{contact.phone}</div> : null}
              {contact.calendarUrl ? (
                <div>
                  <a
                    href={contact.calendarUrl}
                    className="text-brand"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Schedule a call
                  </a>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </main>
      <SiteFooter
        variant="compact"
        showBrandMark
        extraNote="Made with burn-chart.com by Soki Creative LLC"
      />
    </div>
  );
}
